import React, { useCallback, useMemo } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';

import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useAgentStore, useDeckStore } from '@/store';
import Text from '@/components/Typography/Text';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';

const RESOURCE_KIND_META = {
  model: { icon: 'cube', color: '#A855F7' },
  agent: { icon: 'user-astronaut', color: '#10B981' },
  tool: { icon: 'wand-magic-sparkles', color: '#6366F1' },
};

export default function DeckColumnHeader({
  chat,
  columnId,
  columnIndex,
  totalColumns,
  onRemove,
  onChangeChat,
  onChangeResource,
  onDragStart,
  isFocused = false,
  titleOverride,
  resourceKind,
  openExternalHref,
}) {
  const { colors } = useTheme();
  const router = useRouter();
  const { moveColumnLeft, moveColumnRight } = useDeckStore(state => state.actions);
  const getAgentById = useAgentStore(state => state.getAgentById);

  const agent = useMemo(() => {
    if (!chat?.agentId) return null;
    return getAgentById(chat.agentId);
  }, [chat?.agentId, getAgentById]);

  const title = (typeof titleOverride === 'string' && titleOverride.trim())
    ? titleOverride.trim()
    : (chat?.title || 'New Chat');
  const isFirst = columnIndex === 0;
  const isLast = columnIndex === totalColumns - 1;

  const handleOpenFullPage = useCallback(() => {
    if (chat?.id) {
      router.push(`/c/${chat.id}`);
    }
  }, [chat?.id, router]);

  const handleOpenExternal = useCallback(() => {
    if (openExternalHref) {
      router.push(openExternalHref);
    }
  }, [openExternalHref, router]);

  const resourceMeta = resourceKind ? RESOURCE_KIND_META[resourceKind] : null;
  const changeResourceLabel =
    resourceKind === 'model'
      ? 'Change model'
      : resourceKind === 'agent'
        ? 'Change agent'
        : resourceKind === 'tool'
          ? 'Change tool'
          : 'Change resource';

  const handleMoveLeft = useCallback(() => {
    moveColumnLeft(columnId);
  }, [columnId, moveColumnLeft]);

  const handleMoveRight = useCallback(() => {
    moveColumnRight(columnId);
  }, [columnId, moveColumnRight]);

  const handleMouseDown = useCallback((e) => {
    if (onDragStart) onDragStart(e);
  }, [onDragStart]);

  return (
    <View style={[styles.container, { borderBottomColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
      <Pressable
        style={styles.titleArea}
        onMouseDown={handleMouseDown}
      >
        <View style={[styles.columnBadge, { backgroundColor: isFocused ? '#3b82f6' : colors.solid_color_hover }]}>
          <Text size="xxs" weight="bold" style={{ color: isFocused ? '#fff' : colors.text_color_tertiary }}>
            {columnIndex + 1}
          </Text>
        </View>
        {agent && (
          <Icon name="robot" size={14} color={colors.text_color_secondary} />
        )}
        {resourceMeta && !agent && (
          <Icon name={resourceMeta.icon} size={14} color={resourceMeta.color} solid />
        )}
        <Text size="s" weight="semibold" color="primary" numberOfLines={1} style={styles.title}>
          {title}
        </Text>
      </Pressable>

      <View style={styles.actions}>
        {chat?.id && (
          <NavigationBarButton
            icon="arrow-up-right-from-square"
            onPress={handleOpenFullPage}
            tooltip="Open in full page"
            accessibilityLabel="Open conversation in full page"
          />
        )}
        {openExternalHref && !chat?.id && (
          <NavigationBarButton
            icon="arrow-up-right-from-square"
            onPress={handleOpenExternal}
            tooltip="Open in full page"
            accessibilityLabel="Open resource in full page"
          />
        )}

        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <NavigationBarButton
              icon="ellipsis-vertical"
              accessibilityLabel={`Column ${columnIndex + 1} menu`}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="DropdownMenuContent" align="end" sideOffset={5}>
            <DropdownMenuItem
              key="move-left"
              onSelect={handleMoveLeft}
              icon="arrow-left"
              title="Move left"
              isDisabled={isFirst}
            />
            <DropdownMenuItem
              key="move-right"
              onSelect={handleMoveRight}
              icon="arrow-right"
              title="Move right"
              isDisabled={isLast}
            />
            <DropdownMenuItemDivider key="divider" />
            {typeof onChangeChat === 'function' && (
              <DropdownMenuItem
                key="change-chat"
                onSelect={onChangeChat}
                icon="arrow-right-arrow-left"
                title="Change conversation"
              />
            )}
            {typeof onChangeResource === 'function' && (
              <DropdownMenuItem
                key="change-resource"
                onSelect={onChangeResource}
                icon="arrow-right-arrow-left"
                title={changeResourceLabel}
              />
            )}
            {chat?.id && (
              <DropdownMenuItem
                key="open-full"
                onSelect={handleOpenFullPage}
                icon="arrow-up-right-from-square"
                title="Open in full page"
              />
            )}
            {openExternalHref && !chat?.id && (
              <DropdownMenuItem
                key="open-external"
                onSelect={handleOpenExternal}
                icon="arrow-up-right-from-square"
                title="Open in full page"
              />
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        <NavigationBarButton
          icon="xmark"
          onPress={onRemove}
          tooltip="Remove column"
          accessibilityLabel={`Remove column ${columnIndex + 1}`}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 53,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  titleArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    cursor: 'grab',
    overflow: 'hidden',
    marginRight: 8,
  },
  title: {
    flex: 1,
  },
  columnBadge: {
    width: 20,
    height: 20,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
});
